/**
 * ExecutivePage — KPI-Reihe + UpdateLanes (AP-3, Entwurf „HIG").
 *
 * Die KPI-Reihe bleibt unverändert. Darunter stehen die sechs Bahnen
 * *Externe Quellen → Mirror → Knowledge → Generatoren → Ausgabe → Deployment*
 * als Spalten mit Hairline-Chevron dazwischen. Der alte `FlowStrip` (fünf
 * statische Karten ohne eine einzige Zahl) ist ersatzlos entfallen.
 *
 * Zwei Regeln tragen diese Seite:
 *
 *  1. **Keine Zahl wird hier gerechnet.** Bahnköpfe, Knotenwerte, Zustände,
 *     Release-Chips und Kanten kommen vollständig aus `pipeline.json`
 *     (`npm run data`). Was hier steht, ist Auswahl — nie Aggregation.
 *  2. **Der Zoom ist rein additiv.** Die Bahnbreite hängt an der Breite der
 *     Reihe, nie am Inhalt; höhere Stufen fügen nur Zeilen hinzu (Untertitel,
 *     Release-Chips, Ziel-Chip). Nichts springt, keine Spalte ändert ihre Breite.
 *  3. **Unter `EXEC_NARROW_QUERY` kippt die Reihe, statt quer zu scrollen.**
 *     Dieselben Formen, dasselbe Vokabular, dieselben Zustände — die sechs
 *     Bahnen stapeln sich nur, Bahnkopf links, Knoten rechts. Die Anordnung
 *     macht `executive.css` allein; hier steht nur, was CSS nicht entscheiden
 *     kann: Kanten aus (samt sichtbarer Ansage) und getauschte Pfeilachsen.
 *
 * Markup-Regel: ein Knoten ist ein `<div>`, in dem nur der *Kopf* interaktiv
 * ist; Release-Chips und Ziel-Chip sind Geschwister daneben. Das erledigt
 * `FlowNode` — die Chips werden als `children` übergeben.
 *
 * Kopf und Drill-Down-Chips sind echte Links (`href` bzw. `Link`), keine
 * Buttons mit `navigate`: eine Status-Webapp lebt davon, einen Artefakt-Typ
 * oder eine Komponente per Mittelklick nebenbei zu öffnen.
 *
 * ── Zwei Änderungen aus der globalen Formatversion (AP-B) ──────────────────
 *
 *  A. **Der Stand kommt aus der Kopfzeile.** Der seitenlokale Umschalter
 *     (`Alle · 202604 · …`) ist entfallen; `useRelease()` ist die Quelle. Der
 *     Zoom bleibt lokal — er ist Detailgrad DIESER Seite, kein Datenschnitt.
 *     An der Stelle des alten Umschalters steht jetzt eine stille Anzeige, die
 *     sagt, welcher Stand gilt und wo er gewählt wird.
 *
 *  B. **Der Knotenkopf öffnet den Dialog HIER**, statt nach `/architecture`
 *     zu springen. Getragen wird das vom Suchparameter `?komponente=<id>`:
 *     der Kopf bleibt ein echtes `<a href>` (Mittelklick, Link kopieren), ein
 *     geöffneter Dialog ist teilbar, und die Zurück-Taste schließt ihn. Ein
 *     `useState` hätte all das nicht — die Architekturseite kann es über ihre
 *     Route längst, und die Startseite soll nicht weniger können. Der Dialog
 *     selbst ist unverändert `DetailPanel` (prince-ui `Modal`, Fokusfalle,
 *     Esc); nachgebaut wird hier nichts.
 */
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, Icon, KpiCard, Segment, SegmentedControl } from "@conuti-das/prince-ui";
import { princeIcon } from "@/ui-prince";
import { releaseLabel, releaseShortLabel, useRelease } from "@/context";
import {
  FlowEdges,
  FlowNode,
  LaneHeader,
  edgeCountLabel,
  formatCount,
  laneAriaLabel,
  selectLiveEdges,
} from "@/components/flow";
import { DetailPanel } from "@/components/house/DetailPanel";
import { HOUSE_COMPONENTS } from "@/components/house/houseData";
import pipelineJson from "@/data/generated/pipeline.json";
import summary from "@/data/generated/summary.json";
import {
  ALL,
  ARTIFACT_TYPE_LABEL,
  type FlowState,
  type PipelineData,
  type PipelineNode,
  type PipelineReleaseChip,
} from "@/types/pipeline";
import "@/styles/accents.css";
import "./executive.css";

type Summary = {
  sources_count: number;
  repos_count: number;
  artifact_counts: Record<string, Record<string, number>>;
  runs_count: number;
};

/** Detailgrad, nicht Geometrie — semantischer Zoom, kein `transform: scale()`. */
type Zoom = "1" | "2" | "3";

const ZOOM_OPTS: { id: Zoom; label: string }[] = [
  { id: "1", label: "Übersicht" },
  { id: "2", label: "Kennzahlen" },
  { id: "3", label: "Artefakt-Detail" },
];

const pipeline = pipelineJson as unknown as PipelineData;
const nf = (n: number) => n.toLocaleString("de-DE");

const ARROW_KEYS = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];

/**
 * Der Umbruchpunkt der Bahnenreihe — WORTGLEICH mit der Media-Query in
 * `executive.css`. Die Herleitung steht dort im Kopf der Datei: die Reihe
 * braucht auf ihrem Boden 6 × 176 + 5 × 26 = 1.186 px, Sidebar und Polsterungen
 * kosten 340 px, also kippt sie ab 1.526 px Fensterbreite abwärts.
 *
 * Die Anordnung macht das CSS allein; diese Konstante existiert nur für das,
 * was CSS nicht entscheiden kann — ob das Kanten-Overlay noch etwas aussagt und
 * wie die Pfeiltasten liegen. `executiveCss.test.ts` hält beide zusammen.
 */
export const EXEC_NARROW_QUERY = "(max-width: 1525px)";

/**
 * Ist die Reihe gestapelt? Ohne `matchMedia` (jsdom, sehr alte Engines) gilt
 * die breite Anordnung — das ist der Zustand, in dem alles vorhanden ist.
 */
function useNarrowLanes(): boolean {
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(EXEC_NARROW_QUERY);
    const sync = () => setNarrow(mq.matches);
    sync();
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", sync);
      return () => mq.removeEventListener("change", sync);
    }
    // Safari < 14 kennt nur die abgekündigte Variante.
    mq.addListener?.(sync);
    return () => mq.removeListener?.(sync);
  }, []);

  return narrow;
}

/**
 * Release-Chips nach Artefakt-Typ bündeln — eine Zeile je Typ, die Stände als
 * Chips darin. Die knowledge-collection trägt 15 Chips; nebeneinander gelegt
 * wären das 15 Zahlen ohne Zuordnung. Die Reihenfolge ist die des
 * Datenschritts (erstes Auftreten je Typ), damit die Oberfläche keine eigene
 * Sortierung erfindet.
 */
function groupChipsByType(chips: PipelineReleaseChip[]) {
  const groups: { type: string; label: string; chips: PipelineReleaseChip[] }[] = [];
  for (const chip of chips) {
    const type = chip.type ?? "";
    let group = groups.find((g) => g.type === type);
    if (!group) {
      group = { type, label: chip.label ?? ARTIFACT_TYPE_LABEL[type] ?? type, chips: [] };
      groups.push(group);
    }
    group.chips.push(chip);
  }
  return groups;
}

/** Suchparameter, der den Komponenten-Dialog der Startseite trägt. */
export const COMPONENT_PARAM = "komponente";

export function ExecutivePage() {
  const s = summary as Summary;

  // Der Stand ist global (Kopfzeile); der Zoom ist Detailgrad DIESER Seite.
  const { release } = useRelease();
  const [zoom, setZoom] = useState<Zoom>("2");

  // Der offene Dialog steht in der URL — teilbar, und die Zurück-Taste
  // schließt ihn. Ein unbekannter Wert öffnet nichts (toter Lesezeichen-Link).
  const [params, setParams] = useSearchParams();
  const openId = params.get(COMPONENT_PARAM);
  const selected = openId ? HOUSE_COMPONENTS[openId] : undefined;
  const closeDetail = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete(COMPONENT_PARAM);
    // `replace`: Schließen legt keinen Eintrag an, sonst führte die
    // Zurück-Taste direkt in den eben geschlossenen Dialog zurück.
    setParams(next, { replace: true });
  }, [params, setParams]);
  const [highlight, setHighlight] = useState<string | null>(null);
  const [edgeLabel, setEdgeLabel] = useState("");
  const canvasRef = useRef<HTMLDivElement>(null);
  const narrow = useNarrowLanes();

  /**
   * Kanten nur in der breiten Anordnung. Ihre Aussage ist „Bahn n speist
   * Bahn n+1" und lebt von der Leserichtung links → rechts; über gestapelte
   * Bahnen gezogen wäre sie ein Kurvenknäuel statt einer Aussage. Abgeschaltet
   * wird sie deshalb — aber SICHTBAR, in der Fußzeile, an derselben Stelle, an
   * der breit der Kantenzähler steht.
   */
  const edgesEnabled = zoom !== "1" && !narrow;

  const totalArtifacts = Object.values(s.artifact_counts || {})
    .flatMap((r) => Object.values(r))
    .reduce((a, b) => a + b, 0);

  const kpis = [
    { label: "Regulatorische Quellen", value: nf(s.sources_count), link: "/artifacts", accent: true },
    { label: "Repositories", value: nf(s.repos_count), link: "/architecture", accent: false },
    { label: "Artefakte (extrahiert)", value: nf(totalArtifacts), link: "/artifacts", accent: false },
    { label: "Verarbeitungs-Runs", value: nf(s.runs_count), link: "/runs", accent: false },
  ];

  /** Knoten je Bahn, in der Reihenfolge der Bahnen aus dem Datenschritt. */
  const columns = useMemo(
    () => pipeline.lanes.map((lane) => pipeline.nodes.filter((n) => n.lane === lane.id)),
    [],
  );
  const stateOf = useCallback(
    (id: string): FlowState | undefined => pipeline.nodes.find((n) => n.id === id)?.state[release],
    [release],
  );

  /**
   * Die gezeichneten Kanten kommen aus DERSELBEN Quelle wie die Bahnkopfzahl.
   * Läuft beides auseinander, ist das ein Fehler — nicht eine Designfrage.
   */
  const edges = useMemo(
    () => selectLiveEdges(pipeline.edges, release, stateOf),
    [release, stateOf],
  );

  // ── Roving-Tabindex über die Bahnen ────────────────────────────────────────
  // 25 Knoten wären 25 Tab-Stopps. Stattdessen ein Einstieg, danach Pfeiltasten
  // (links/rechts = Bahn, hoch/runter = Knoten) und Home/End. Die Bahnen
  // bleiben `role="group"` mit dem Aggregat im Namen; ein `role="grid"` würde
  // Zeilen-Semantik behaupten, die dieses Spalten-Layout nicht hat.
  const [cursor, setCursor] = useState<[number, number]>([0, 0]);
  const activeId = columns[cursor[0]]?.[cursor[1]]?.id ?? columns[0]?.[0]?.id;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.querySelectorAll<HTMLElement>("[data-flow-node]").forEach((el) => {
      el.tabIndex = el.dataset.flowNode === activeId ? 0 : -1;
    });
  }, [activeId, zoom, release]);

  const focusNode = (col: number, row: number) => {
    setCursor([col, row]);
    const id = columns[col]?.[row]?.id;
    if (!id) return;
    canvasRef.current
      ?.querySelector<HTMLElement>(`[data-flow-node="${id}"]`)
      ?.focus();
  };

  // Die Pfeiltasten folgen dem, was auf dem Schirm steht: breit liegen die
  // Bahnen nebeneinander (links/rechts = Bahn, hoch/runter = Knoten), schmal
  // untereinander und ihre Knoten nebeneinander — die beiden Achsen tauschen.
  // Die Reihenfolge selbst bleibt in beiden Anordnungen dieselbe.
  const axis = narrow
    ? { lanePrev: "ArrowUp", laneNext: "ArrowDown", nodePrev: "ArrowLeft", nodeNext: "ArrowRight" }
    : { lanePrev: "ArrowLeft", laneNext: "ArrowRight", nodePrev: "ArrowUp", nodeNext: "ArrowDown" };

  const onLanesKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!ARROW_KEYS.includes(e.key)) return;
    const [col, row] = cursor;
    let nc = col;
    let nr = row;
    if (e.key === axis.lanePrev) nc = Math.max(0, col - 1);
    else if (e.key === axis.laneNext) nc = Math.min(columns.length - 1, col + 1);
    else if (e.key === axis.nodePrev) nr = row - 1;
    else if (e.key === axis.nodeNext) nr = row + 1;
    else if (e.key === "Home") nr = 0;
    else if (e.key === "End") nr = Number.MAX_SAFE_INTEGER;
    const last = (columns[nc]?.length ?? 1) - 1;
    nr = Math.max(0, Math.min(nr, last));
    if (nc === col && nr === row) return;
    e.preventDefault();
    focusNode(nc, nr);
  };

  /** Klick oder Tab auf einen Knoten setzt den Einstiegspunkt neu. */
  const onLanesFocus = (e: ReactFocusEvent<HTMLDivElement>) => {
    const id = (e.target as HTMLElement).dataset?.flowNode;
    if (!id) return;
    for (let c = 0; c < columns.length; c += 1) {
      const r = columns[c].findIndex((n) => n.id === id);
      if (r >= 0) {
        setCursor([c, r]);
        return;
      }
    }
  };

  const runs = pipeline.runsByRelease?.[release] ?? [];
  const runsWord =
    runs.length === 0
      ? "kein Lauf erfasst"
      : `${runs.length} ${runs.length === 1 ? "Lauf" : "Läufe"} erfasst`;

  const renderNode = (node: PipelineNode) => {
    const componentId = node.componentId ?? node.id;
    const state = node.state?.[release] ?? "ghost";
    const value = node.value?.[release] ?? null;
    const groups =
      zoom === "3"
        ? groupChipsByType(
            (node.releaseChips ?? []).filter((c) => release === ALL || c.release === release),
          )
        : [];

    return (
      <FlowNode
        key={node.id}
        id={node.id}
        state={state}
        title={node.label}
        subtitle={zoom === "3" ? node.sub : undefined}
        value={formatCount(value)}
        label={node.label}
        // Relativ zur Startseite: der Browser macht daraus `/?komponente=…`.
        // Ein echter Link, aber einer, der auf der Startseite bleibt.
        href={`?${COMPONENT_PARAM}=${encodeURIComponent(componentId)}`}
        current={selected?.id === componentId}
        onHighlight={setHighlight}
      >
        {groups.length > 0 && (
          <div className="exec-rels">
            {groups.map((g) => (
              <div className="exec-rel-row" key={g.type || node.id}>
                {g.label && <span className="exec-rel-type">{g.label}</span>}
                {g.chips.map((c) => {
                  // Bei gewähltem Stand steht der Stand schon im Umschalter und
                  // in der Fußzeile — im Chip bliebe er reine Wiederholung.
                  const text =
                    release === ALL ? `${c.release} · ${formatCount(c.count)}` : formatCount(c.count);
                  const name =
                    `${g.label} ${c.release} — ${formatCount(c.count)} Artefakte` +
                    (c.consultation ? ", Konsultationsfassung" : "");
                  const consultation = c.consultation ? "" : undefined;
                  return c.drillDown ? (
                    <Link
                      key={`${g.type}-${c.release}`}
                      to={c.drillDown}
                      className="exec-relchip"
                      data-consultation={consultation}
                      aria-label={`${name}, öffnet ${c.drillDown}`}
                      title={`${name} — öffnet ${c.drillDown}`}
                    >
                      {text}
                    </Link>
                  ) : (
                    <span
                      key={`${g.type}-${c.release}`}
                      className="exec-relchip"
                      data-consultation={consultation}
                      title={name}
                    >
                      {text}
                    </span>
                  );
                })}
              </div>
            ))}
          </div>
        )}
        {/* Hier stand der Ziel-Chip aus `node.goalRef`. Die Ziele leben jetzt
            ausschließlich auf `/umsetzungsgrad`; `goalRef` bleibt in den Daten,
            wird auf der Startseite aber nicht mehr gezeigt. */}
      </FlowNode>
    );
  };

  return (
    <div className="page page--lanes">
      <div className="page-header">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="accent-dot" aria-hidden="true" />
          <h2 className="page-title" style={{ margin: 0 }}>Executive Übersicht</h2>
        </div>
        <div className="page-subtitle">Der Zustand der regu2code-Plattform auf einen Blick.</div>
        <hr className="fine-line" style={{ marginTop: 12 }} />
      </div>

      <div className="exec-kpis">
        {kpis.map((k) => (
          <Link key={k.label} to={k.link} style={{ textDecoration: "none", display: "block" }}>
            <KpiCard label={k.label} value={k.value} accent={k.accent} />
          </Link>
        ))}
      </div>

      <section className="exec-flow" aria-label="Verarbeitungs-Pipeline">
        <div className="exec-flow-bar">
          <div className="exec-flow-head">
            <span className="accent-dot" aria-hidden="true" />
            <span className="exec-flow-title">Von der Quelle zum deploybaren Artefakt</span>
          </div>
          <div className="exec-flow-controls">
            {/* An der Stelle des alten Stand-Umschalters: keine Steuerung, nur
                die Auskunft, welcher Stand gilt und wo er umgestellt wird.
                Stumm wegzulassen hieße, den Wechsel als Verlust zu zeigen. */}
            <span
              className="exec-flow-stand"
              title={`Formatversion ${releaseLabel(release)} — Auswahl in der Kopfzeile, gilt für alle Seiten.`}
            >
              <span className="exec-flow-stand-label">Formatversion</span>
              <span className="exec-flow-stand-value">{releaseShortLabel(release)}</span>
              <span className="exec-flow-stand-hint">Kopfzeile</span>
            </span>
            <SegmentedControl
              aria-label="Zoomstufe"
              size="s"
              selectedKeys={new Set([zoom])}
              onSelectionChange={(keys) => {
                const first = [...keys][0];
                if (typeof first === "string") setZoom(first as Zoom);
              }}
            >
              {ZOOM_OPTS.map((o) => (
                <Segment key={o.id} id={o.id}>{o.label}</Segment>
              ))}
            </SegmentedControl>
          </div>
        </div>

        <div className="exec-flow-scroll">
          {/* `position: relative` — Voraussetzung für das absolute Kanten-Overlay. */}
          <div className="exec-flow-canvas" ref={canvasRef}>
            <FlowEdges
              canvasRef={canvasRef}
              edges={edges}
              enabled={edgesEnabled}
              highlight={highlight}
              onGeometry={(g) => setEdgeLabel(edgeCountLabel(g))}
            />
            <div
              className="exec-lanes"
              data-zoom={zoom}
              onKeyDown={onLanesKeyDown}
              onFocus={onLanesFocus}
            >
              {pipeline.lanes.map((lane, i) => (
                <Fragment key={lane.id}>
                  {i > 0 && (
                    <div className="exec-lane-gap" aria-hidden="true">
                      <span className="exec-chev">›</span>
                    </div>
                  )}
                  <div className="exec-lane" role="group" aria-label={laneAriaLabel(lane, release)}>
                    <LaneHeader lane={lane} release={release} compact={zoom === "1"} />
                    {zoom !== "1" && (
                      <div className="exec-lane-body">{columns[i].map(renderNode)}</div>
                    )}
                  </div>
                </Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Die Fußzeile trägt den Kantenzähler — und in der schmalen Anordnung
            an genau derselben Stelle den Grund, warum es keinen gibt. Kanten
            still verschwinden zu lassen wäre die einzige Stelle, an der schmal
            und breit verschieden viel behaupten. */}
        <p className="exec-flow-caption">
          {release === ALL ? "Alle Stände" : `Stand ${release}`} · {runsWord}
          {zoom !== "1" &&
            (narrow ? " · Kanten ausgeblendet (gestapelte Ansicht)" : edgeLabel && ` · ${edgeLabel}`)}
        </p>
      </section>

      <div style={{ display: "flex", justifyContent: "center", marginTop: "var(--prn-space)" }}>
        <Link to="/architecture" style={{ textDecoration: "none" }}>
          <Button variant="filled">
            Zur Architektur-Ebene
            <Icon name={princeIcon("navigation-right-arrow")} size={16} />
          </Button>
        </Link>
      </div>

      {/* Derselbe Dialog wie auf der Architekturseite — importiert, nicht
          nachgebaut. Er bringt Fokusfalle, Esc und Fokus-Rückgabe mit. */}
      <DetailPanel component={selected} onClose={closeDetail} />
    </div>
  );
}
